export type ActivityId =
    | "kana"
    | "kanjiWriting"
    | "kanjiMemoryQuiz"
    | "lessons"
    | "grammar"
    | "vocabulary"
    | "conjugation"
    | "speaking";

export type ActivityDefinition = { id: ActivityId; features: string[]; to: string };

export type ActivityPeriod = "today" | "yesterday" | "days" | "week" | "month";

export type ActivitySummary = {
    period: ActivityPeriod;
    // Only set when period === "days": the exact number of calendar days
    // since the activity, e.g. 5 for "5 days ago".
    dayCount?: number;
    activities: ActivityId[];
    primary: ActivityDefinition;
    kind: "single" | "few" | "many";
    wider: { period: "week" | "month"; count: number } | null;
};

type ProgressRow = { data?: unknown; updatedAt?: string } | null | undefined;

export const ACTIVITIES: ActivityDefinition[] = [
    { id: "kana", features: ["kanaDrawCounts", "kanaListenCounts"], to: "/kana-writing-practice" },
    { id: "kanjiWriting", features: ["kanjiDrawCounts", "kanjiListenCounts"], to: "/kanji-writing-practice" },
    {
        id: "kanjiMemoryQuiz",
        features: ["kanjiMemory", "kanjiMemorySeenCounts", "kanjiMemoryGuessStats", "kanjiMemoryNotes"],
        to: "/kanji-memory-quiz",
    },
    { id: "lessons", features: ["lessonProgress", "lessonNotes", "lessonParagraphNotes"], to: "/lessons" },
    { id: "grammar", features: ["grammarProgress"], to: "/grammar" },
    { id: "vocabulary", features: ["vocabularyProgress", "vocabularyListenCounts"], to: "/vocabulary" },
    { id: "conjugation", features: ["conjugationProgress", "conjugationListenCounts"], to: "/conjugation" },
    { id: "speaking", features: ["speakingCounts"], to: "/speaking-practice" },
];

export const ACTIVITY_FEATURES = ACTIVITIES.flatMap((activity) => activity.features);

const MANY_ACTIVITIES = 4;

// Beyond "today"/"yesterday", an exact day count ("5 days ago") is more
// useful to a learner than the vague "this week"/"this month" bucket it
// replaces for that range - see activitySummaryCopy.ts and homePage.json's
// activitySummary.periods.days. Older than this, the calendar week/month
// bucket copy below takes over instead.
const MAX_EXACT_DAYS = 13;

function hasContent(data: unknown): boolean {
    if (data === null || data === undefined) return false;
    if (typeof data === "object") return Object.keys(data as object).length > 0;
    return true;
}

export function lastActiveByActivity(rows: Record<string, ProgressRow>): Partial<Record<ActivityId, Date>> {
    const result: Partial<Record<ActivityId, Date>> = {};
    ACTIVITIES.forEach((activity) => {
        activity.features.forEach((feature) => {
            const row = rows?.[feature];
            if (!row || !hasContent(row.data) || !row.updatedAt) return;
            const time = new Date(row.updatedAt);
            if (Number.isNaN(time.getTime())) return;
            const current = result[activity.id];
            if (!current || time > current) result[activity.id] = time;
        });
    });
    return result;
}

function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Calendar-date arithmetic (not elapsed-ms), so this stays correct across a
// DST transition the same way startOfDay already does.
function daysBefore(today: Date, count: number): Date {
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() - count);
}

export function periodStarts(now: Date): Record<"today" | "yesterday" | "week" | "month", Date> {
    const today = startOfDay(now);
    const yesterday = daysBefore(today, 1);
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const week = daysBefore(today, daysSinceMonday);
    const month = new Date(today.getFullYear(), today.getMonth(), 1);
    return { today, yesterday, week, month };
}

function activitiesSince(lastActive: Partial<Record<ActivityId, Date>>, from: Date, until?: Date): ActivityId[] {
    return ACTIVITIES.map((activity) => activity.id)
        .filter((id) => {
            const time = lastActive[id];
            return time !== undefined && time >= from && (!until || time < until);
        })
        .sort((a, b) => (lastActive[b] as Date).getTime() - (lastActive[a] as Date).getTime());
}

export function summarizeRecentActivity(
    lastActive: Partial<Record<ActivityId, Date>>,
    now: Date = new Date()
): ActivitySummary | null {
    const starts = periodStarts(now);
    const today = starts.today;
    // Today, yesterday, then one exact calendar day at a time (2-13 days
    // ago) - each still a single day, so "activities" always means "done on
    // that specific day", exactly like today/yesterday already did. Only
    // once nothing matches any specific day do the broader calendar
    // week/month buckets take over as a fallback.
    const candidates: { period: ActivityPeriod; dayCount?: number; activities: ActivityId[] }[] = [
        { period: "today", activities: activitiesSince(lastActive, starts.today) },
        { period: "yesterday", activities: activitiesSince(lastActive, starts.yesterday, starts.today) },
    ];
    for (let dayCount = 2; dayCount <= MAX_EXACT_DAYS; dayCount += 1) {
        candidates.push({
            period: "days",
            dayCount,
            activities: activitiesSince(lastActive, daysBefore(today, dayCount), daysBefore(today, dayCount - 1)),
        });
    }
    candidates.push({ period: "week", activities: activitiesSince(lastActive, starts.week) });
    candidates.push({ period: "month", activities: activitiesSince(lastActive, starts.month) });

    const chosen = candidates.find((candidate) => candidate.activities.length > 0);
    if (!chosen) return null;

    // The "wider" blurb is always the next tier up from whichever single day
    // (today/yesterday/N days ago) was chosen - "week" if that day still
    // falls within the current calendar week, otherwise "month" (e.g. a
    // Monday whose "yesterday" was last Sunday, a different calendar week).
    // A chosen "month" bucket has nothing broader to compare itself against;
    // "week" doesn't either in practice (the day-by-day loop above always
    // finds it first), but falls back to "month" for safety.
    let chosenDate: Date | null = null;
    if (chosen.period === "today") chosenDate = starts.today;
    else if (chosen.period === "yesterday") chosenDate = starts.yesterday;
    else if (chosen.period === "days") chosenDate = daysBefore(today, chosen.dayCount as number);

    let widerPeriod: "week" | "month" | null;
    if (chosen.period === "month") {
        widerPeriod = null;
    } else if (chosenDate && chosenDate >= starts.week) {
        widerPeriod = "week";
    } else {
        widerPeriod = "month";
    }
    const widerCount = widerPeriod ? activitiesSince(lastActive, starts[widerPeriod]).length : 0;
    const count = chosen.activities.length;

    return {
        period: chosen.period,
        dayCount: chosen.dayCount,
        activities: chosen.activities,
        primary: ACTIVITIES.find((activity) => activity.id === chosen.activities[0]) as ActivityDefinition,
        kind: count === 1 ? "single" : count >= MANY_ACTIVITIES ? "many" : "few",
        wider: widerPeriod && widerCount > count ? { period: widerPeriod, count: widerCount } : null,
    };
}
